import React, { FC } from 'react';
import { Supplier } from 'Quotes/types/supplier';
import { presentAccessMethodForSupplier, presentSupplierNameForPriceTile } from 'Quotes/utils/supplierHelpers';
import PresentForRoles from 'Auth/PresentForRoles';
import { currencyFormatter } from 'shared/utils/currencyFormatter';
import { formatBandwidthForSupplier } from 'shared/utils/connectionCapacity';
import { userPermissions } from 'shared/utils/permissions';
import { IPriceData, IQuote } from 'Quotes/types/store';
import { PriceCard } from '../PriceCard';
import { BandwidthLinkComponent, SupplierImageComponent } from '../OpticalP2P/Components';
import { BearerType } from 'Quotes/QuoteBuilder/components/Configure/Bearer/BearerType';

const identifierPrefix = 'P2P';

interface IP2PProps {
  selected?: boolean;
  onClick?: () => void;
  aEndAccessMethod: IQuote['aEndAccessMethod'];
  bEndAccessMethod: IQuote['bEndAccessMethod'];
  aEndBandwidth: IQuote['aEndBandwidth'];
  bEndBandwidth: IQuote['bEndBandwidth'];
  aEndPostcode: string;
  aEndSupplier: Supplier;
  bEndPostcode: string;
  amortisedAnnualPrice: number | null;
  bEndSupplier: Supplier;
  aEndPrice: number | null;
  bEndPrice: number | null;
  priceId: string | null;
  className?: string;
  readonly: boolean;
  product_sub_type: IPriceData['product_sub_type'];
  bandwidth?: string;
  term_length_in_months?: IPriceData['term_length_in_months'];
  isPoA?: boolean;
  supportsSecondaryCircuits: boolean;
  is10GbE?: boolean;
  aEndPort?: BearerType;
  bEndPort?: BearerType;
}

export const P2P: FC<React.PropsWithChildren<IP2PProps>> = ({
  selected = false,
  onClick,
  amortisedAnnualPrice,
  aEndAccessMethod,
  bEndAccessMethod,
  aEndBandwidth,
  bEndBandwidth,
  aEndPostcode,
  aEndSupplier,
  bEndPostcode,
  bEndSupplier,
  aEndPrice,
  bEndPrice,
  priceId,
  readonly,
  product_sub_type,
  bandwidth,
  term_length_in_months,
  isPoA = false,
  supportsSecondaryCircuits,
  is10GbE,
  aEndPort,
  bEndPort,
}) => {
  return (
    <PriceCard
      identifierPrefix={identifierPrefix}
      priceId={priceId!}
      readonly={readonly}
      selected={selected}
      onClick={onClick}
      aEndPostcode={aEndPostcode}
      bEndPostcode={bEndPostcode}
      amortisedAnnualPrice={amortisedAnnualPrice}
      product_sub_type={product_sub_type}
      term_length_in_months={term_length_in_months}
      bandwidth={bandwidth}
      isPoA={isPoA}
      supportsSecondaryCircuits={supportsSecondaryCircuits}
      is10GbE={is10GbE}
    >
      <ul className="list-unstyled">
        <SupplierImageComponent supplier={aEndSupplier} />
        <li data-testid="a-end-supplier">{presentSupplierNameForPriceTile(aEndSupplier)}</li>
        {aEndBandwidth && <li className="text-secondary a-end-bandwidth">Bandwidth: {formatBandwidthForSupplier(aEndBandwidth, aEndSupplier)}</li>}
        {aEndAccessMethod && 
          <li className="text-secondary a-end-access-method">
            <div dangerouslySetInnerHTML={{ __html: presentAccessMethodForSupplier(aEndSupplier, aEndAccessMethod, aEndBandwidth, aEndPort) }} />
          </li>
        }
        <PresentForRoles roles={userPermissions.costs}>
          <li className="text-secondary a-end-cost">Cost: {currencyFormatter.format(aEndPrice ?? 0)}</li>
        </PresentForRoles>
        <BandwidthLinkComponent priceId={priceId} />
        <SupplierImageComponent supplier={bEndSupplier} />
        <li data-testid="b-end-supplier">{presentSupplierNameForPriceTile(bEndSupplier)}</li>
        {bEndBandwidth && <li className="text-secondary b-end-bandwidth">Bandwidth: {formatBandwidthForSupplier(bEndBandwidth, bEndSupplier)}</li>}
        {bEndAccessMethod && 
          <li className="text-secondary b-end-access-method">
            <div dangerouslySetInnerHTML={{ __html: presentAccessMethodForSupplier(bEndSupplier, bEndAccessMethod, bEndBandwidth, bEndPort) }} />
          </li>
        }
        <PresentForRoles roles={userPermissions.costs}>
          <li className="text-secondary mb-0 b-end-cost">Cost: {currencyFormatter.format(bEndPrice ?? 0)}</li>
        </PresentForRoles>
      </ul>
    </PriceCard>
  );
};

export const StyledP2P = P2P;
